import { Box, Center, Flex, Spinner } from "@chakra-ui/react";
import { Link } from "react-router";
import CDS from "design-system";
import { useMemo } from "react";
import useCaminoStore from "hooks/useCaminoStore";
import { createQueryString } from "helpers/queryString";
import { useTaskListQuery } from "features/Resources/Task/hooks";
import { OPEN_TASK_STATUSES } from "features/Resources/Task/constants";
import { TaskCard, TaskListHeader } from "features/Resources/Task/Components/UserTaskSummary";
import { Task } from "features/Resources/Task/models";
import { getOpenTasksSorted } from "features/Resources/Task/helpers/stats";
import useSideDrawer from "hooks/useSideDrawer";
import ActionSideDrawer from "components/ActionSideDrawer";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import SectionHeader from "components/SectionHeader";

const MAX_VISIBLE = 3;

export default function MyTasksCard() {
  const user = useCaminoStore((state) => state.currentUser);

  const userId = user?.id;
  const queryString = useMemo(() => {
    if (!userId) return "";
    return createQueryString({
      additionalQueries: {
        status: [...OPEN_TASK_STATUSES],
      },
      filters: [{ fieldName: "assignee_id", value: userId, operator: "=" }],
    });
  }, [userId]);

  const { data: tasks = [], isLoading } = useTaskListQuery(queryString, { enabled: !!userId });

  const sortedTasks = useMemo(() => getOpenTasksSorted(tasks), [tasks]);

  const subtitle =
    sortedTasks.length > 0
      ? `Showing ${Math.min(sortedTasks.length, MAX_VISIBLE)} of ${sortedTasks.length} Open Tasks`
      : undefined;
  const visibleTasks = sortedTasks.slice(0, MAX_VISIBLE);
  const remainingCount = sortedTasks.length - MAX_VISIBLE;

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<Task>({
    getItemId: (item) => item?.id,
  });

  return (
    <>
      <Flex direction="column" width="100%">
        <SectionHeader title="Tasks" subtitle={subtitle} linkTo={SHARED_ROUTES.myTasks} />

        {isLoading && (
          <Center py={4}>
            <Spinner size="sm" />
          </Center>
        )}

        {!isLoading && tasks.length === 0 && (
          <CDS.Text12 color="gray.400" py={4}>
            No open tasks
          </CDS.Text12>
        )}

        {!isLoading && visibleTasks.length > 0 && (
          <Box>
            <TaskListHeader showJourney showAssignee={false} />
            {visibleTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                compact
                onClick={(t) => expandClickHandlerByItem(t)}
                showAssignee={false}
                showJourney
              />
            ))}
            {remainingCount > 0 && (
              <CDS.Text12
                as={Link}
                {...{ to: SHARED_ROUTES.myTasks }}
                display="block"
                color="brand.500"
                fontWeight="500"
                px={6}
                py={3}
                _hover={{ textDecoration: "underline", bg: "gray.50" }}
              >
                +{remainingCount} more task{remainingCount !== 1 ? "s" : ""} — view all
              </CDS.Text12>
            )}
          </Box>
        )}
      </Flex>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />
      <TaskActionModalByQueryParam />
    </>
  );
}
